import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
// Removed Checkbox, Title imports
import { Modal, Form, Input, Button, Spin, message, Row, Col, InputNumber, Card, Descriptions, List, Tag, Empty, Alert, Typography, Radio } from 'antd'; // Added Radio
import PhysicalExamModal from './PhysicalExamModal';
import moment from 'moment';
import { useAuth } from '../context/AuthContext';
// Removed surgicalHistoryProcedures import

const { TextArea } = Input;
const { Text } = Typography; // Removed Title

// Helper to format date - unchanged
const formatDate = (dateString) => {
    return dateString ? moment(dateString).format('L') : 'N/A';
};

const PatientIntakeForm = ({ patientId, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loadingPatientData, setLoadingPatientData] = useState(false);
  const [patientData, setPatientData] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [showPhysicalExam, setShowPhysicalExam] = useState(false);
  const [physicalExamData, setPhysicalExamData] = useState({});
  const { user } = useAuth();

  // Removed helper functions for checklist

  const fetchInitialData = useCallback(async () => {
      if (!patientId) {
          setPatientData(null);
          form.resetFields();
          return;
      };
      console.log("Intake form fetching initial data for patient:", patientId);
      setLoadingPatientData(true);
      setFetchError(null);
      try {
          // Fetch patient summary data (REMOVE /api/v1 prefix)
          const patientRes = await axios.get(`/patients/${patientId}`);
          setPatientData(patientRes.data.data.patient);

          // Fetch today's intake record (REMOVE /api/v1 prefix)
          const intakeRes = await axios.get(`/patients/${patientId}/intakes`);
          const todaysIntake = intakeRes.data.data.intakeRecord;

          if (todaysIntake) {
              console.log("Found today's intake record, pre-filling form:", todaysIntake);

              const vitalsData = todaysIntake.vitals || {};
              const bodyCompData = todaysIntake.bodyComposition || {};
              // Removed surgicalHistoryData variable

              if (todaysIntake.physicalExam) {
                setPhysicalExamData(todaysIntake.physicalExam);
              }

              form.setFieldsValue({
                  reasonForVisit: todaysIntake.reasonForVisit,
                  historyUpdates: todaysIntake.historyUpdates,
                  isPregnant: todaysIntake.isPregnant, // Pre-fill pregnancy status
                  isPostMenopausal: todaysIntake.isPostMenopausal, // Pre-fill menopausal status
                  vitals: {
                    bloodPressureSystolic: vitalsData.bloodPressureSystolic,
                    bloodPressureDiastolic: vitalsData.bloodPressureDiastolic,
                    heartRate: vitalsData.heartRate,
                    respiratoryRate: vitalsData.respiratoryRate,
                    oxygenSaturation: vitalsData.oxygenSaturation,
                    temperature: vitalsData.temperature?.value,
                    weight: vitalsData.weight?.value,
                    height: vitalsData.height?.value,
                    painLevel: vitalsData.painLevel,
                  },
                  bodyComposition: {
                    biaBodyWaterPercent: bodyCompData.biaBodyWaterPercent,
                    biaBodyFatPercent: bodyCompData.biaBodyFatPercent,
                    biaSkeletalMusclePercent: bodyCompData.biaSkeletalMusclePercent,
                    biaVisceralFatLevel: bodyCompData.biaVisceralFatLevel,
                  },
                  // Removed structuredSurgicalHistory from setFieldsValue
              });
          } else {
              console.log("No intake record found for today, resetting form.");
              form.resetFields(); // Reset form
          }

      } catch (err) {
          console.error("Error fetching initial data for intake form:", err);
          setFetchError(err.response?.data?.message || 'Failed to load initial data.');
          setPatientData(null);
          form.resetFields(); // Reset form on error
      } finally {
          setLoadingPatientData(false);
      }
  }, [patientId, form]);

  useEffect(() => {
      fetchInitialData();
  }, [fetchInitialData]);

  const handleFinish = async (values) => {
    if (!user || !user._id) {
      message.error('User not logged in or user ID missing.');
      return;
    }
    if (!patientId) {
       message.error('Patient ID is missing.');
       return;
    }

    setLoading(true);
    try {
      const payload = {
        reasonForVisit: values.reasonForVisit,
        historyUpdates: values.historyUpdates,
        isPregnant: values.isPregnant === 'pregnant' ? true : (values.isPregnant === 'not_pregnant' ? false : undefined), // Map to boolean or undefined
        isPostMenopausal: values.isPostMenopausal === 'post_menopausal' ? true : (values.isPostMenopausal === 'pre_menopausal' || values.isPostMenopausal === 'peri_menopausal' ? false : undefined), // Map to boolean or undefined
        vitals: {
            height: values.vitals?.height ? { value: values.vitals.height, unit: 'cm' } : undefined,
            weight: values.vitals?.weight ? { value: values.vitals.weight, unit: 'kg' } : undefined,
            temperature: values.vitals?.temperature ? { value: values.vitals.temperature, unit: 'C' } : undefined,
            bloodPressureSystolic: values.vitals?.bloodPressureSystolic,
            bloodPressureDiastolic: values.vitals?.bloodPressureDiastolic,
            heartRate: values.vitals?.heartRate,
            respiratoryRate: values.vitals?.respiratoryRate,
            oxygenSaturation: values.vitals?.oxygenSaturation,
            painLevel: values.vitals?.painLevel,
        },
        bodyComposition: {
            biaBodyWaterPercent: values.bodyComposition?.biaBodyWaterPercent,
            biaBodyFatPercent: values.bodyComposition?.biaBodyFatPercent,
            biaSkeletalMusclePercent: values.bodyComposition?.biaSkeletalMusclePercent,
            biaVisceralFatLevel: values.bodyComposition?.biaVisceralFatLevel,
             method: 'BIA',
        },
        // Removed structuredSurgicalHistory from payload
        physicalExam: physicalExamData
      };

      if (payload.vitals && Object.values(payload.vitals).every(v => v === undefined || (typeof v === 'object' && v !== null && v.value === undefined))) {
          delete payload.vitals;
      }
       if (payload.bodyComposition && Object.values(payload.bodyComposition).every(v => v === undefined)) {
          delete payload.bodyComposition;
      }

      // POST to the nested route (REMOVE /api/v1 prefix)
      await axios.post(`/patients/${patientId}/intakes`, payload);
      message.success('Intake record saved successfully!');
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error("Error saving intake record:", err);
      message.error(err.response?.data?.message || 'Failed to save intake record.');
    } finally {
      setLoading(false);
    }
  };

  // Extract and flatten only the procedure arrays from the patient's permanent structured surgical history
  const permanentSurgicalHistoryList = patientData?.structuredSurgicalHistory
    ? Object.entries(patientData.structuredSurgicalHistory)
        .filter(([key, value]) => Array.isArray(value)) // Keep only entries where the value is an array
        .map(([key, value]) => value) // Get just the arrays
        .flat() // Flatten the array of arrays
        .filter(Boolean) // Remove any null/undefined/empty strings
    : [];

  return (
      <Spin spinning={loading || loadingPatientData}>
        {fetchError && <Alert message={fetchError} type="error" showIcon style={{ marginBottom: 16 }} />}

        {/* Display Patient History Summaries (Read-Only) - unchanged */}
        {patientData && !loadingPatientData && (
             <Card size="small" title="Current Record Summary (Read-Only)" style={{ marginBottom: 16, background: '#f9f9f9' }}>
                {/* Allergies */}
                <Descriptions title="Active Allergies" size="small" column={1} style={{ marginBottom: 10 }}>
                    <Descriptions.Item>
                        {patientData.allergies?.filter(a => a.status === 'Active').length > 0 ? (
                            <List size="small" dataSource={patientData.allergies.filter(a => a.status === 'Active')} renderItem={item => {
                                // console.log('Rendering Allergy item:', item); // DEBUG LOG Removed
                                const substanceText = (typeof item.substance === 'object' && item.substance?.name) ? item.substance.name : (item.substance || 'N/A');
                                const reactionText = item.reaction || 'N/A';
                                return <List.Item>{String(substanceText)} - {String(reactionText)}</List.Item>;
                            }} />
                        ) : <Text type="secondary">None Recorded</Text>}
                    </Descriptions.Item>
                </Descriptions>
                 {/* Medications */}
                 <Descriptions title="Active Medications" size="small" column={1} style={{ marginBottom: 10 }}>
                    <Descriptions.Item>
                         {patientData.medications?.filter(m => m.status === 'Active').length > 0 ? (
                            <List size="small" dataSource={patientData.medications.filter(m => m.status === 'Active')} renderItem={item => {
                                // console.log('Rendering Medication item:', item); // DEBUG LOG Removed
                                const medNameText = (typeof item.medicationName === 'object' && item.medicationName?.name) ? item.medicationName.name : (item.medicationName || 'N/A');
                                const dosageText = item.dosage || '';
                                const frequencyText = item.frequency || '';
                                return <List.Item>{String(medNameText)} {String(dosageText)} {String(frequencyText)}</List.Item>;
                            }} />
                        ) : <Text type="secondary">None Recorded</Text>}
                    </Descriptions.Item>
                </Descriptions>
                 {/* Active Problem List */}
                 <Descriptions title="Active Problem List" size="small" column={1} style={{ marginBottom: 10 }}>
                    <Descriptions.Item>
                         {patientData.problems?.filter(p => p.status === 'Active').length > 0 ? (
                            <List size="small" dataSource={patientData.problems.filter(p => p.status === 'Active')} renderItem={item => {
                                // console.log('Rendering Problem item:', item); // DEBUG LOG Removed
                                const problemText = (typeof item.problemName === 'object' && item.problemName?.name) ? item.problemName.name : (item.problemName || 'N/A');
                                const dateText = item.dateOfOnset ? `(${formatDate(item.dateOfOnset)})` : '';
                                return <List.Item>{String(problemText)} {dateText}</List.Item>;
                            }} />
                         ) : <Text type="secondary">None Recorded</Text>}
                    </Descriptions.Item>
                </Descriptions>
                 {/* Surgical History (from permanent structured record) */}
                 <Descriptions title="Surgical History" size="small" column={1} style={{ marginBottom: 10 }}>
                     <Descriptions.Item>
                         {permanentSurgicalHistoryList.length > 0 ? (
                            <List size="small" dataSource={permanentSurgicalHistoryList} renderItem={item => <List.Item>{item}</List.Item>} />
                         ) : <Text type="secondary">None Recorded (in permanent checklist)</Text>}
                    </Descriptions.Item>
                </Descriptions>
                {/* Removed "Added This Visit" section */}
            </Card>
        )}

        <Form form={form} layout="vertical" onFinish={handleFinish}>
          <Form.Item name="reasonForVisit" label="Reason for Visit Today" rules={[{ required: true, message: 'Please enter the reason for visit.' }]}>
            <TextArea rows={3} />
          </Form.Item>

          <Form.Item name="historyUpdates" label="History Updates (Changes since last visit?)">
            <TextArea rows={3} placeholder="e.g., New medications, recent hospitalizations, changes in symptoms..." />
          </Form.Item>

          {/* --- Pregnancy and Menopausal Status --- */}
          {patientData?.gender === 'Female' && (
            <Card size="small" title="Women's Health Status" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item name="isPregnant" label="Pregnancy Status">
                    <Radio.Group>
                      <Radio value="not_pregnant">Not Pregnant</Radio>
                      <Radio value="pregnant">Pregnant</Radio>
                      <Radio value="unknown_preg">Unknown</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="isPostMenopausal" label="Menopausal Status">
                    <Radio.Group>
                      <Radio value="pre_menopausal">Pre-menopausal</Radio>
                      <Radio value="peri_menopausal">Peri-menopausal</Radio>
                      <Radio value="post_menopausal">Post-menopausal</Radio>
                      <Radio value="unknown_meno">Unknown / Not Applicable</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          )}

          {/* --- Vitals Section --- */}
          {/* Removed extra prop containing the button */}
          <Card size="small" title="Vital Signs" style={{marginBottom: 16}}>
            <Row gutter={16}>
               <Col xs={12} sm={8} md={6}><Form.Item label="BP (Sys/Dia mmHg)"><Input.Group compact><Form.Item name={['vitals', 'bloodPressureSystolic']} noStyle><InputNumber style={{ width: '50%' }} placeholder="Sys" /></Form.Item><Form.Item name={['vitals', 'bloodPressureDiastolic']} noStyle><InputNumber style={{ width: '50%' }} placeholder="Dia" /></Form.Item></Input.Group></Form.Item></Col>
               <Col xs={12} sm={8} md={6}><Form.Item name={['vitals', 'heartRate']} label="HR (BPM)"><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
               <Col xs={12} sm={8} md={6}><Form.Item name={['vitals', 'respiratoryRate']} label="RR (breaths/min)"><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
               <Col xs={12} sm={8} md={6}><Form.Item name={['vitals', 'oxygenSaturation']} label="SpO2 (%)"><InputNumber style={{ width: '100%' }} min={0} max={100} /></Form.Item></Col>
               <Col xs={12} sm={8} md={6}><Form.Item name={['vitals', 'temperature']} label="Temp (°C)"><InputNumber style={{ width: '100%' }} step={0.1} /></Form.Item></Col>
               <Col xs={12} sm={8} md={6}><Form.Item name={['vitals', 'weight']} label="Weight (kg)"><InputNumber style={{ width: '100%' }} step={0.1} /></Form.Item></Col>
               <Col xs={12} sm={8} md={6}><Form.Item name={['vitals', 'height']} label="Height (cm)"><InputNumber style={{ width: '100%' }} step={0.1} /></Form.Item></Col>
               <Col xs={12} sm={8} md={6}><Form.Item name={['vitals', 'painLevel']} label="Pain Level (0-10)"><InputNumber style={{ width: '100%' }} min={0} max={10} /></Form.Item></Col>
            </Row>
          </Card>

           {/* --- Body Composition Section (Unchanged from previous corrected version) --- */}
           <Card size="small" title="Body Composition (BIA)" style={{marginBottom: 16}}>
             <Row gutter={16}>
               <Col xs={12} sm={6}><Form.Item name={['bodyComposition', 'biaVisceralFatLevel']} label="Visceral Fat Level"><InputNumber style={{ width: '100%' }} min={0} max={59} /></Form.Item></Col>
               <Col xs={12} sm={6}><Form.Item name={['bodyComposition', 'biaBodyFatPercent']} label="Body Fat (%)"><InputNumber style={{ width: '100%' }} min={0} max={100} step={0.1} /></Form.Item></Col>
               <Col xs={12} sm={6}><Form.Item name={['bodyComposition', 'biaSkeletalMusclePercent']} label="Skeletal Muscle (%)"><InputNumber style={{ width: '100%' }} min={0} max={100} step={0.1} /></Form.Item></Col>
               <Col xs={12} sm={6}><Form.Item name={['bodyComposition', 'biaBodyWaterPercent']} label="Body Water (%)"><InputNumber style={{ width: '100%' }} min={0} max={100} step={0.1} /></Form.Item></Col>
             </Row>
           </Card>

          {/* Physical Exam Modal */}
          <PhysicalExamModal
            show={showPhysicalExam}
            onHide={() => setShowPhysicalExam(false)}
            initialData={physicalExamData}
            onSave={(data) => setPhysicalExamData(data)}
            patientName={patientData?.name}
          />

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              Save Intake Record
            </Button>
            {/* form.resetFields() remains unchanged */}
            <Button style={{ marginLeft: 8 }} onClick={() => { form.resetFields(); }}>
              Clear Form
            </Button>
          </Form.Item>
        </Form>
      </Spin>
  );
};

export default PatientIntakeForm;
